/**
 * Variant-B Sidequest Author editor.
 *
 * Entered from RunnerScene's pause overlay via `scene.pause("runner")` +
 * `scene.launch("SidequestAuthorScene", { returnTo: "runner", walletSource:
 * "runner" })`. On Save / Cancel the scene stops itself and resumes the
 * runner; RunnerScene's RESUME listener then drains any pending sidequests
 * out of `AuthoredSidequestService` and spawns the portal in the world at
 * the hero's current position.
 *
 * Inputs (matches the game's controller-first posture):
 *  - ↑/↓ or D-pad up/down — move focus between fields.
 *  - ←/→ on Name/Author — cycle the curated pool of values.
 *  - ←/→ on Puzzle/Biome radios — cycle the value.
 *  - ←/→ on a set-piece chip — jump to the other column.
 *  - Y / A / Enter — toggle / activate the focused element.
 *  - B / Esc — cancel (with confirm modal if the form was edited).
 *
 * Free-text escape hatch: typing any a-z / 0-9 / _ on the laptop keyboard
 * while a Name or Author field is focused switches THAT field into free-text
 * mode; ←/→ on the field reverts to pool mode (discards the typed text). Per
 * the spec, no preference is persisted — every editor open starts in pool
 * mode again.
 */

import Phaser from "phaser";

import { NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import { t } from "../i18n";
import type { StringKey } from "../i18n";
import type { AuthoredSidequestInput, AuthorIdentity, Biome, PuzzleKind } from "../types";

import { AuthoredSidequestService } from "../services/AuthoredSidequestService";
import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { AUTHOR_NAME_POOL } from "../services/authorNamePool";
import { InputService } from "../services/InputService";
import { hashString } from "../services/seededRng";
import { SET_PIECE_CATALOG } from "../services/setPieceCatalog";
import { SIDEQUEST_NAME_POOL } from "../services/sidequestNamePool";
import {
  LauncherWallet,
  makeDefaultWalletSnapshot,
  type Wallet,
  type WalletSnapshot,
} from "../services/Wallet";

export interface SidequestAuthorSceneInit {
  returnTo: "runner" | "launcher";
  walletSource: "runner" | "launcher";
}

type FieldMode = "pool" | "freetext";

interface TextFieldState {
  mode: FieldMode;
  poolIdx: number;
  freeText: string;
}

const PUZZLE_KINDS: readonly PuzzleKind[] = ["kills", "crystals", "lever"];
const BIOMES: readonly Biome[] = ["forrest", "snowy", "mysterious"];

const PUZZLE_LABEL_KEYS: Record<PuzzleKind, StringKey> = {
  kills: "author.puzzle.kills",
  crystals: "author.puzzle.crystals",
  lever: "author.puzzle.lever",
};
const BIOME_LABEL_KEYS: Record<Biome, StringKey> = {
  forrest: "biome.forrest",
  snowy: "biome.snowy",
  mysterious: "biome.mysterious",
};

const AUTHOR_MAX_LEN = 10;
const NAME_MAX_LEN = 18;

// Focus-index layout — matches the row order rendered in `buildLayout`.
const FOCUS_NAME = 0;
const FOCUS_AUTHOR = 1;
const FOCUS_PUZZLE = 2;
const FOCUS_BIOME = 3;
const FOCUS_SETPIECE_FIRST = 4;
const FOCUS_SETPIECE_LAST = FOCUS_SETPIECE_FIRST + SET_PIECE_CATALOG.length - 1;
const FOCUS_CANCEL = FOCUS_SETPIECE_LAST + 1;
const FOCUS_SAVE = FOCUS_CANCEL + 1;
const FOCUS_COUNT = FOCUS_SAVE + 1;

export class SidequestAuthorScene extends Phaser.Scene {
  static readonly KEY = "SidequestAuthorScene";

  private returnTo: SidequestAuthorSceneInit["returnTo"] = "runner";
  private walletSource: SidequestAuthorSceneInit["walletSource"] = "runner";
  private enteredAtMs = 0;

  private input2!: InputService;
  private wallet!: Wallet;
  private service!: AuthoredSidequestService;
  private identityService!: AuthorIdentityService;
  private telemetry!: {
    emit: (l: 1 | 2 | 3, t: string, d?: Record<string, number | string | boolean>) => void;
  };

  private focusIdx = FOCUS_NAME;
  private nameField!: TextFieldState;
  private authorField!: TextFieldState;
  private puzzleIdx = 0;
  private biomeIdx = 0;
  private setPieceToggled = new Set<string>();
  private initialSnapshot = "";

  private rowContainers: Phaser.GameObjects.Container[] = [];
  private rowFocusBgs: (Phaser.GameObjects.Rectangle | undefined)[] = [];
  private nameValueText!: Phaser.GameObjects.Text;
  private nameChevronLeft!: Phaser.GameObjects.Text;
  private nameChevronRight!: Phaser.GameObjects.Text;
  private authorValueText!: Phaser.GameObjects.Text;
  private authorChevronLeft!: Phaser.GameObjects.Text;
  private authorChevronRight!: Phaser.GameObjects.Text;
  private authorRowAnchorY = 0;
  private puzzleRadioTexts: Phaser.GameObjects.Text[] = [];
  private biomeRadioTexts: Phaser.GameObjects.Text[] = [];
  private setPieceChips: { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
  private cancelButton!: { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
  private saveButton!: { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
  private costLine!: Phaser.GameObjects.Text;
  private avatarPreview?: Phaser.GameObjects.Image;
  private hintLine!: Phaser.GameObjects.Text;
  private disabledTooltip!: Phaser.GameObjects.Text;
  private discardModal?: Phaser.GameObjects.Container;
  private keyboardHandler?: (ev: KeyboardEvent) => void;
  private prevMoveX = 0;
  private prevMoveY = 0;

  constructor() {
    super({ key: SidequestAuthorScene.KEY });
  }

  init(data: Partial<SidequestAuthorSceneInit>): void {
    this.returnTo = data.returnTo ?? "runner";
    this.walletSource = data.walletSource ?? "runner";
  }

  create(): void {
    this.enteredAtMs = this.time.now;
    this.input2 = new InputService(this);

    this.identityService = this.registry.get("authorIdentityService") as AuthorIdentityService;
    if (!this.identityService) {
      this.identityService = new AuthorIdentityService();
      this.registry.set("authorIdentityService", this.identityService);
    }

    const telemetry = this.registry.get("telemetryService") as
      | { emit: (l: 1 | 2 | 3, t: string, d?: Record<string, number | string | boolean>) => void }
      | undefined;
    this.telemetry = telemetry ?? { emit: () => undefined };

    this.service = AuthoredSidequestService.getOrCreate({
      game: this.game,
      emitTelemetry: (l, tag, d) => this.telemetry.emit(l, tag, d),
      now: () => this.time.now,
    });

    this.wallet = this.resolveWallet();

    // Random starting picks (re-rolled each editor open per spec).
    this.nameField = {
      mode: "pool",
      poolIdx: Math.floor(Math.random() * SIDEQUEST_NAME_POOL.length),
      freeText: "",
    };
    this.authorField = {
      mode: "pool",
      poolIdx: Math.floor(Math.random() * AUTHOR_NAME_POOL.length),
      freeText: "",
    };
    this.puzzleIdx = 0;
    this.biomeIdx = 0;
    // Default: one set piece pre-toggled (`bird_trio` / "Vogelschwarm")
    // so every authored sub-level has at least one piece of content.
    // The player can deselect it freely.
    this.setPieceToggled = new Set<string>(["bird_trio"]);
    this.initialSnapshot = this.snapshot();

    this.buildLayout();
    this.refreshAll();

    this.keyboardHandler = (ev: KeyboardEvent) => this.onRawKey(ev);
    this.input.keyboard?.on("keydown", this.keyboardHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (this.keyboardHandler) this.input.keyboard?.off("keydown", this.keyboardHandler);
      this.keyboardHandler = undefined;
    });

    this.telemetry.emit(1, "ugc.sidequest.editor.open", { source: "pause_menu" });
  }

  override update(): void {
    if (this.discardModal) {
      this.updateDiscardModal();
      return;
    }
    const input = this.input2.read();

    // Esc/B → cancel.
    if (input.pausePressed) {
      this.requestCancel();
      return;
    }

    // Edge-triggered ↑/↓ for focus, ←/→ for field-specific cycle.
    const dx = input.moveX > 0.5 ? 1 : input.moveX < -0.5 ? -1 : 0;
    const dy = input.moveY > 0.5 ? 1 : input.moveY < -0.5 ? -1 : 0;
    if (dy !== this.prevMoveY) {
      this.prevMoveY = dy;
      if (dy !== 0) this.moveFocus(dy);
    }
    if (dx !== this.prevMoveX) {
      this.prevMoveX = dx;
      if (dx !== 0) this.cycleOrJump(dx);
    }

    // Y / Interact = activate.
    if (input.interactPressed || input.attackPressed || input.jumpPressed) {
      this.activateFocused();
    }
  }

  // -- layout ---------------------------------------------------------------

  private buildLayout(): void {
    // Scenes can be `create()`d more than once across the game's
    // lifetime (the player opens the editor, saves, then re-opens
    // it). Phaser destroys the underlying GameObjects on scene
    // shutdown, but our instance arrays still hold references to the
    // dead objects. Reset them here so `buildLayout`'s push/index
    // assignments start from a clean slate and refreshSetPieceChips
    // doesn't try to setText() on a destroyed label.
    this.rowContainers = [];
    this.rowFocusBgs = [];
    this.puzzleRadioTexts = [];
    this.biomeRadioTexts = [];
    this.setPieceChips = [];
    this.avatarPreview = undefined;

    const panel = this.add
      .rectangle(NATIVE_WIDTH / 2, NATIVE_HEIGHT / 2, NATIVE_WIDTH - 8, NATIVE_HEIGHT - 8, PALETTE.authorPanelBg, 0.98)
      .setStrokeStyle(2, PALETTE.authorPanelEdge);
    void panel;

    // Cyan UGC corner-mark, top-left of the panel.
    this.add.rectangle(8, 8, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);

    // Banner.
    this.add
      .text(NATIVE_WIDTH / 2, 14, t("author.title"), {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.toHex(PALETTE.authorPanelEdge),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    // Avatar preview lives inside the Author row (rendered live by
    // refreshAvatarPreview); no top-right slot.
    this.avatarPreview = undefined;

    let y = 30;

    // Name row.
    const nameRow = this.makeRow(FOCUS_NAME, y, t("author.field.name"));
    this.nameChevronLeft = this.add
      .text(70, y, "<", { fontFamily: "monospace", fontSize: "9px", color: this.toHex(PALETTE.authorChevron) })
      .setOrigin(0.5, 0.5);
    this.nameValueText = this.add
      .text(NATIVE_WIDTH / 2, y, "", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.ink),
      })
      .setOrigin(0.5, 0.5);
    this.nameChevronRight = this.add
      .text(NATIVE_WIDTH - 70, y, ">", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.authorChevron),
      })
      .setOrigin(0.5, 0.5);
    nameRow.add([this.nameChevronLeft, this.nameValueText, this.nameChevronRight]);
    this.rowContainers[FOCUS_NAME] = nameRow;
    y += 14;

    // Author row — chevrons on the outside, avatar (16×16) just inside
    // the left chevron, value text right of the avatar.
    const authorRow = this.makeRow(FOCUS_AUTHOR, y, t("author.field.author"));
    this.authorRowAnchorY = y;
    this.authorChevronLeft = this.add
      .text(70, y, "<", { fontFamily: "monospace", fontSize: "9px", color: this.toHex(PALETTE.authorChevron) })
      .setOrigin(0.5, 0.5);
    this.authorValueText = this.add
      .text(NATIVE_WIDTH / 2 + 8, y, "", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.ink),
      })
      .setOrigin(0.5, 0.5);
    this.authorChevronRight = this.add
      .text(NATIVE_WIDTH - 70, y, ">", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.authorChevron),
      })
      .setOrigin(0.5, 0.5);
    authorRow.add([this.authorChevronLeft, this.authorValueText, this.authorChevronRight]);
    this.rowContainers[FOCUS_AUTHOR] = authorRow;
    y += 16;

    // Puzzle radio.
    const puzzleRow = this.makeRow(FOCUS_PUZZLE, y, t("author.field.puzzle"));
    const puzzleLabels: Record<PuzzleKind, string> = {
      kills: t("author.puzzle.kills"),
      crystals: t("author.puzzle.crystals"),
      lever: t("author.puzzle.lever"),
    };
    let px = 70;
    this.puzzleRadioTexts = PUZZLE_KINDS.map((kind) => {
      const txt = this.add
        .text(px, y, `( ) ${puzzleLabels[kind]}`, {
          fontFamily: "monospace",
          fontSize: "8px",
          color: this.toHex(PALETTE.ink),
        })
        .setOrigin(0, 0.5);
      puzzleRow.add(txt);
      px += 90;
      return txt;
    });
    this.rowContainers[FOCUS_PUZZLE] = puzzleRow;
    y += 14;

    // Biome radio.
    const biomeRow = this.makeRow(FOCUS_BIOME, y, t("author.field.biome"));
    const biomeLabels: Record<Biome, string> = {
      forrest: t("biome.forrest"),
      snowy: t("biome.snowy"),
      mysterious: t("biome.mysterious"),
    };
    let bx = 70;
    this.biomeRadioTexts = BIOMES.map((b) => {
      const txt = this.add
        .text(bx, y, `( ) ${biomeLabels[b]}`, {
          fontFamily: "monospace",
          fontSize: "8px",
          color: this.toHex(PALETTE.ink),
        })
        .setOrigin(0, 0.5);
      biomeRow.add(txt);
      bx += 90;
      return txt;
    });
    this.rowContainers[FOCUS_BIOME] = biomeRow;
    y += 16;

    // Set-piece chips — 2 columns × 4 rows.
    this.add
      .text(12, y, `${t("author.field.setpieces")}  (${t("author.field.setpieces.hint")})`, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.authorPanelEdge),
      })
      .setOrigin(0, 0.5);
    y += 12;

    const CHIP_W = 130;
    const CHIP_H = 14;
    const COLS = 2;
    SET_PIECE_CATALOG.forEach((sp, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = 16 + col * (CHIP_W + 10) + CHIP_W / 2;
      const cy = y + row * (CHIP_H + 4) + CHIP_H / 2;
      const focusBg = this.add
        .rectangle(cx, cy, CHIP_W + 4, CHIP_H + 4, UGC_CYAN, 0)
        .setOrigin(0.5, 0.5);
      const box = this.add
        .rectangle(cx, cy, CHIP_W, CHIP_H, PALETTE.authorChipBg, 0.95)
        .setOrigin(0.5, 0.5)
        .setStrokeStyle(1, PALETTE.authorPanelEdge);
      const swatch = this.add.rectangle(cx - CHIP_W / 2 + 8, cy, 6, 6, sp.iconColor, 1).setOrigin(0.5, 0.5);
      const label = this.add
        .text(cx - CHIP_W / 2 + 18, cy, `[ ] ${t(sp.labelKey)}`, {
          fontFamily: "monospace",
          fontSize: "7px",
          color: this.toHex(PALETTE.hudText),
        })
        .setOrigin(0, 0.5);
      this.setPieceChips.push({ box, label });
      const rowContainer = this.add.container(0, 0);
      rowContainer.add([focusBg, box, swatch, label]);
      this.rowContainers[FOCUS_SETPIECE_FIRST + i] = rowContainer;
      this.rowFocusBgs[FOCUS_SETPIECE_FIRST + i] = focusBg;
    });
    y += 4 * (CHIP_H + 4) + 4;

    // Cost line.
    this.costLine = this.add
      .text(NATIVE_WIDTH / 2, y, "", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.ink),
      })
      .setOrigin(0.5, 0.5);
    y += 12;

    // Cancel + Save buttons.
    const buttonY = NATIVE_HEIGHT - 16;
    this.cancelButton = this.makeButton(60, buttonY, t("author.button.cancel"));
    this.saveButton = this.makeButton(NATIVE_WIDTH - 100, buttonY, t("author.button.save"));
    const cancelFocusBg = this.add
      .rectangle(60, buttonY, 84, 18, UGC_CYAN, 0)
      .setOrigin(0.5, 0.5);
    const saveFocusBg = this.add
      .rectangle(NATIVE_WIDTH - 100, buttonY, 84, 18, UGC_CYAN, 0)
      .setOrigin(0.5, 0.5);
    const cancelContainer = this.add.container(0, 0);
    cancelContainer.add([cancelFocusBg, this.cancelButton.box, this.cancelButton.label]);
    this.rowContainers[FOCUS_CANCEL] = cancelContainer;
    this.rowFocusBgs[FOCUS_CANCEL] = cancelFocusBg;
    const saveContainer = this.add.container(0, 0);
    saveContainer.add([saveFocusBg, this.saveButton.box, this.saveButton.label]);
    this.rowContainers[FOCUS_SAVE] = saveContainer;
    this.rowFocusBgs[FOCUS_SAVE] = saveFocusBg;

    // Hint + disabled tooltip line.
    this.hintLine = this.add
      .text(NATIVE_WIDTH / 2, buttonY - 12, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.authorPanelEdge),
      })
      .setOrigin(0.5, 0.5);
    this.disabledTooltip = this.add
      .text(NATIVE_WIDTH - 100, buttonY - 12, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hp),
      })
      .setOrigin(0.5, 0.5);
  }

  private makeRow(focusIdx: number, y: number, labelText: string): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    // Focus background — invisible by default, tinted cyan when this row
    // is the focused one. Full row width minus side padding. Stored at
    // rowFocusBgs[focusIdx] so refreshFocusOutlines can toggle alpha.
    const bgWidth = NATIVE_WIDTH - 16;
    const bg = this.add
      .rectangle(NATIVE_WIDTH / 2, y, bgWidth, 14, UGC_CYAN, 0)
      .setOrigin(0.5, 0.5);
    c.add(bg);
    this.rowFocusBgs[focusIdx] = bg;
    const label = this.add
      .text(12, y, `${labelText}:`, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.authorPanelEdge),
      })
      .setOrigin(0, 0.5);
    c.add(label);
    return c;
  }

  private makeButton(x: number, y: number, labelText: string): { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text } {
    const box = this.add
      .rectangle(x, y, 80, 14, PALETTE.authorChipBg, 0.95)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, PALETTE.authorPanelEdge);
    const label = this.add
      .text(x, y, labelText, {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudText),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    box.setInteractive({ useHandCursor: true });
    return { box, label };
  }

  // -- refresh / paint ------------------------------------------------------

  private refreshAll(): void {
    this.refreshTextField(this.nameField, this.nameValueText, this.nameChevronLeft, this.nameChevronRight, SIDEQUEST_NAME_POOL);
    this.refreshTextField(this.authorField, this.authorValueText, this.authorChevronLeft, this.authorChevronRight, AUTHOR_NAME_POOL);
    this.refreshAvatarPreview();
    this.refreshRadio(this.puzzleRadioTexts, this.puzzleIdx, (k) => t(PUZZLE_LABEL_KEYS[PUZZLE_KINDS[k]!]));
    this.refreshRadio(this.biomeRadioTexts, this.biomeIdx, (k) => t(BIOME_LABEL_KEYS[BIOMES[k]!]));
    this.refreshSetPieceChips();
    this.refreshCostAndButtons();
    this.refreshFocusOutlines();
    this.refreshHint();
  }

  private refreshTextField(
    field: TextFieldState,
    valueText: Phaser.GameObjects.Text,
    leftCh: Phaser.GameObjects.Text,
    rightCh: Phaser.GameObjects.Text,
    pool: readonly string[],
  ): void {
    const value = field.mode === "pool" ? pool[field.poolIdx]! : field.freeText;
    if (field.mode === "freetext") {
      valueText.setText(`${value}|`);
      leftCh.setVisible(false);
      rightCh.setVisible(false);
    } else {
      valueText.setText(value);
      leftCh.setVisible(true);
      rightCh.setVisible(true);
    }
  }

  private refreshAvatarPreview(): void {
    if (this.avatarPreview) {
      this.avatarPreview.destroy();
      this.avatarPreview = undefined;
    }
    const username = this.currentAuthorUsername();
    if (!username) return;
    const identity: AuthorIdentity = this.identityService.forSeed(hashString(`author:${username}`));
    const size = 12;
    const x = NATIVE_WIDTH / 2 - 30;
    const y = this.authorRowAnchorY - size / 2;
    this.avatarPreview = this.identityService.drawCircleAvatar(this, x, y, identity, size);
    // Attach to the Author row container so the focus highlight (which
    // sits behind the row) layers correctly underneath the avatar.
    const authorRow = this.rowContainers[FOCUS_AUTHOR];
    if (authorRow) authorRow.add(this.avatarPreview);
  }

  private refreshRadio(
    texts: Phaser.GameObjects.Text[],
    activeIdx: number,
    label: (i: number) => string,
  ): void {
    texts.forEach((txt, i) => {
      const marker = i === activeIdx ? "•" : " ";
      txt.setText(`(${marker}) ${label(i)}`);
    });
  }

  private refreshSetPieceChips(): void {
    SET_PIECE_CATALOG.forEach((sp, i) => {
      const chip = this.setPieceChips[i]!;
      const on = this.setPieceToggled.has(sp.id);
      chip.box.setFillStyle(on ? PALETTE.authorChipBgToggled : PALETTE.authorChipBg, 0.95);
      chip.label.setText(`${on ? "[✓]" : "[ ]"} ${t(sp.labelKey)}`);
    });
  }

  private refreshCostAndButtons(): void {
    const cost = AuthoredSidequestService.SCROLL_COST;
    const have = this.scrollsOwned();
    this.costLine.setText(t("author.cost.line", { cost, have }));
    const canSave = this.saveIsEnabled();
    this.saveButton.label.setColor(this.toHex(canSave ? PALETTE.hudText : PALETTE.authorDisabled));
    this.saveButton.box.setStrokeStyle(1, canSave ? PALETTE.authorPanelEdge : PALETTE.authorDisabled);
  }

  private refreshFocusOutlines(): void {
    // Each row owns a dedicated focus background rectangle (built in
    // buildLayout). Show only the focused one, with a tinted UGC-cyan
    // fill + matching stroke for unmistakable visual feedback.
    this.rowFocusBgs.forEach((bg, i) => {
      if (!bg) return;
      const isFocused = i === this.focusIdx;
      bg.setFillStyle(UGC_CYAN, isFocused ? 0.22 : 0);
      bg.setStrokeStyle(isFocused ? 2 : 0, UGC_CYAN);
    });
  }

  private refreshHint(): void {
    let hint = "";
    if (this.focusIdx === FOCUS_NAME || this.focusIdx === FOCUS_AUTHOR) {
      const field = this.focusIdx === FOCUS_NAME ? this.nameField : this.authorField;
      hint = field.mode === "pool" ? t("author.hint.pool") : t("author.hint.freetext");
    }
    this.hintLine.setText(hint);
    this.disabledTooltip.setText(this.saveIsEnabled() ? "" : this.disabledReason());
  }

  // -- helpers --------------------------------------------------------------

  private currentAuthorUsername(): string {
    return this.authorField.mode === "pool"
      ? AUTHOR_NAME_POOL[this.authorField.poolIdx]!
      : this.authorField.freeText;
  }

  private currentSidequestName(): string {
    return this.nameField.mode === "pool"
      ? SIDEQUEST_NAME_POOL[this.nameField.poolIdx]!
      : this.nameField.freeText;
  }

  private scrollsOwned(): number {
    return this.wallet.inventory.filter((i) => i.kind === "scroll").length;
  }

  private saveIsEnabled(): boolean {
    if (!this.currentSidequestName().trim()) return false;
    if (!this.currentAuthorUsername().trim()) return false;
    if (this.scrollsOwned() < AuthoredSidequestService.SCROLL_COST) return false;
    return true;
  }

  private disabledReason(): string {
    if (!this.currentAuthorUsername().trim()) return t("author.disabled.no_author");
    if (!this.currentSidequestName().trim()) return t("author.disabled.no_name");
    if (this.scrollsOwned() < AuthoredSidequestService.SCROLL_COST) return t("author.disabled.no_scroll");
    return "";
  }

  private snapshot(): string {
    return JSON.stringify({
      name: this.currentSidequestName(),
      author: this.currentAuthorUsername(),
      puzzle: PUZZLE_KINDS[this.puzzleIdx],
      biome: BIOMES[this.biomeIdx],
      pieces: [...this.setPieceToggled].sort(),
    });
  }

  private hasEdits(): boolean {
    return this.snapshot() !== this.initialSnapshot;
  }

  // -- input ----------------------------------------------------------------

  private moveFocus(dir: number): void {
    this.focusIdx = (this.focusIdx + dir + FOCUS_COUNT) % FOCUS_COUNT;
    this.refreshFocusOutlines();
    this.refreshHint();
  }

  private cycleOrJump(dir: number): void {
    if (this.focusIdx === FOCUS_NAME) {
      if (this.nameField.mode === "freetext") {
        const flashEmpty = this.nameField.freeText.length > 0;
        this.nameField = { mode: "pool", poolIdx: this.nameField.poolIdx, freeText: "" };
        if (flashEmpty) this.flashField(this.nameValueText);
      } else {
        const next = (this.nameField.poolIdx + dir + SIDEQUEST_NAME_POOL.length) % SIDEQUEST_NAME_POOL.length;
        this.nameField.poolIdx = next;
      }
      this.refreshAll();
      this.telemetry.emit(1, "ugc.sidequest.editor.field.edit", { field: "name", mode: this.nameField.mode });
    } else if (this.focusIdx === FOCUS_AUTHOR) {
      if (this.authorField.mode === "freetext") {
        const flashEmpty = this.authorField.freeText.length > 0;
        this.authorField = { mode: "pool", poolIdx: this.authorField.poolIdx, freeText: "" };
        if (flashEmpty) this.flashField(this.authorValueText);
      } else {
        const next = (this.authorField.poolIdx + dir + AUTHOR_NAME_POOL.length) % AUTHOR_NAME_POOL.length;
        this.authorField.poolIdx = next;
      }
      this.refreshAll();
      this.telemetry.emit(1, "ugc.sidequest.editor.field.edit", { field: "author", mode: this.authorField.mode });
    } else if (this.focusIdx === FOCUS_PUZZLE) {
      this.puzzleIdx = (this.puzzleIdx + dir + PUZZLE_KINDS.length) % PUZZLE_KINDS.length;
      this.refreshAll();
      this.telemetry.emit(1, "ugc.sidequest.editor.field.edit", { field: "puzzle", mode: "pool" });
    } else if (this.focusIdx === FOCUS_BIOME) {
      this.biomeIdx = (this.biomeIdx + dir + BIOMES.length) % BIOMES.length;
      this.refreshAll();
      this.telemetry.emit(1, "ugc.sidequest.editor.field.edit", { field: "biome", mode: "pool" });
    } else if (this.focusIdx >= FOCUS_SETPIECE_FIRST && this.focusIdx <= FOCUS_SETPIECE_LAST) {
      // Jump column: even↔odd swap.
      const localIdx = this.focusIdx - FOCUS_SETPIECE_FIRST;
      const targetLocal = localIdx + (dir > 0 ? (localIdx % 2 === 0 ? 1 : -1) : (localIdx % 2 === 0 ? -1 : 1));
      if (targetLocal >= 0 && targetLocal < SET_PIECE_CATALOG.length) {
        this.focusIdx = FOCUS_SETPIECE_FIRST + targetLocal;
        this.refreshFocusOutlines();
      }
    } else if (this.focusIdx === FOCUS_CANCEL && dir > 0) {
      this.focusIdx = FOCUS_SAVE;
      this.refreshFocusOutlines();
    } else if (this.focusIdx === FOCUS_SAVE && dir < 0) {
      this.focusIdx = FOCUS_CANCEL;
      this.refreshFocusOutlines();
    }
  }

  private activateFocused(): void {
    if (this.focusIdx === FOCUS_PUZZLE) {
      this.puzzleIdx = (this.puzzleIdx + 1) % PUZZLE_KINDS.length;
      this.refreshAll();
    } else if (this.focusIdx === FOCUS_BIOME) {
      this.biomeIdx = (this.biomeIdx + 1) % BIOMES.length;
      this.refreshAll();
    } else if (this.focusIdx >= FOCUS_SETPIECE_FIRST && this.focusIdx <= FOCUS_SETPIECE_LAST) {
      const sp = SET_PIECE_CATALOG[this.focusIdx - FOCUS_SETPIECE_FIRST]!;
      const next = !this.setPieceToggled.has(sp.id);
      if (next) this.setPieceToggled.add(sp.id);
      else this.setPieceToggled.delete(sp.id);
      this.refreshAll();
      this.telemetry.emit(1, "ugc.sidequest.setpiece.toggle", { setPieceId: sp.id, enabled: next });
    } else if (this.focusIdx === FOCUS_CANCEL) {
      this.requestCancel();
    } else if (this.focusIdx === FOCUS_SAVE) {
      this.attemptSave();
    }
  }

  private onRawKey(ev: KeyboardEvent): void {
    if (this.discardModal) return;
    if (this.focusIdx !== FOCUS_NAME && this.focusIdx !== FOCUS_AUTHOR) return;
    const field = this.focusIdx === FOCUS_NAME ? this.nameField : this.authorField;
    const cap = this.focusIdx === FOCUS_NAME ? NAME_MAX_LEN : AUTHOR_MAX_LEN;
    const key = ev.key;
    if (key === "Backspace") {
      if (field.mode === "freetext") {
        field.freeText = field.freeText.slice(0, -1);
        this.refreshAll();
        ev.preventDefault();
      }
      return;
    }
    if (key.length !== 1) return;
    const isLetter = /^[a-zA-Z]$/.test(key);
    const isAuthorChar = /^[a-z0-9_]$/i.test(key);
    if (this.focusIdx === FOCUS_AUTHOR && !isAuthorChar) return;
    if (this.focusIdx === FOCUS_NAME && !isLetter && !/^[a-zA-Z0-9 ]$/.test(key)) return;
    if (field.mode === "pool") {
      // Switch into free-text mode; seed with the typed character.
      const charNormalized = this.focusIdx === FOCUS_AUTHOR ? key.toLowerCase() : key;
      this.assignFieldFreetext(charNormalized);
    } else if (field.freeText.length < cap) {
      const charNormalized = this.focusIdx === FOCUS_AUTHOR ? key.toLowerCase() : key;
      field.freeText += charNormalized;
      this.refreshAll();
    }
    ev.preventDefault();
  }

  private assignFieldFreetext(seed: string): void {
    if (this.focusIdx === FOCUS_NAME) {
      this.nameField = { mode: "freetext", poolIdx: this.nameField.poolIdx, freeText: seed };
    } else {
      this.authorField = { mode: "freetext", poolIdx: this.authorField.poolIdx, freeText: seed };
    }
    this.refreshAll();
  }

  private flashField(textObj: Phaser.GameObjects.Text): void {
    const original = textObj.style.color ?? "#000000";
    textObj.setColor(this.toHex(PALETTE.hp));
    this.time.delayedCall(200, () => textObj.setColor(original));
  }

  // -- save / cancel --------------------------------------------------------

  private attemptSave(): void {
    if (!this.saveIsEnabled()) return;
    const input: AuthoredSidequestInput = {
      authorUsername: this.currentAuthorUsername().trim(),
      name: this.currentSidequestName().trim(),
      puzzleKind: PUZZLE_KINDS[this.puzzleIdx]!,
      biome: BIOMES[this.biomeIdx]!,
      setPieceIds: [...this.setPieceToggled],
    };
    let created;
    try {
      created = this.service.create(input, this.wallet);
    } catch (err) {
      // Wallet race — refresh and bail.
      console.warn("[author] create failed:", err);
      this.refreshAll();
      return;
    }
    this.telemetry.emit(1, "ugc.sidequest.editor.save", { sidequestId: created.id });
    this.exit();
  }

  private requestCancel(): void {
    if (!this.hasEdits()) {
      this.cancelImmediate();
      return;
    }
    this.openDiscardModal();
  }

  private cancelImmediate(): void {
    const dwellMs = this.time.now - this.enteredAtMs;
    this.telemetry.emit(1, "ugc.sidequest.editor.cancel", { dwellMs, hadEdits: this.hasEdits() });
    this.exit();
  }

  private exit(): void {
    if (this.returnTo === "runner") {
      this.scene.stop();
      this.scene.resume("runner");
    } else {
      this.scene.stop();
      this.scene.resume("launcher");
    }
  }

  private openDiscardModal(): void {
    if (this.discardModal) return;
    const c = this.add.container(0, 0).setDepth(60);
    const dim = this.add.rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, 0x000000, 0.6).setOrigin(0, 0);
    const box = this.add
      .rectangle(NATIVE_WIDTH / 2, NATIVE_HEIGHT / 2, 220, 70, PALETTE.authorPanelBg, 1)
      .setStrokeStyle(2, PALETTE.authorPanelEdge);
    const title = this.add
      .text(NATIVE_WIDTH / 2, NATIVE_HEIGHT / 2 - 10, t("author.discard.title"), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.ink),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    const sub = this.add
      .text(NATIVE_WIDTH / 2, NATIVE_HEIGHT / 2 + 6, t("author.discard.confirm"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.authorPanelEdge),
      })
      .setOrigin(0.5, 0.5);
    c.add([dim, box, title, sub]);
    this.discardModal = c;
  }

  private updateDiscardModal(): void {
    const input = this.input2.read();
    if (input.interactPressed || input.attackPressed) {
      this.discardModal?.destroy();
      this.discardModal = undefined;
      this.cancelImmediate();
    } else if (input.pausePressed || input.jumpPressed) {
      this.discardModal?.destroy();
      this.discardModal = undefined;
    }
  }

  // -- wallet wiring --------------------------------------------------------

  private resolveWallet(): Wallet {
    if (this.walletSource === "runner") {
      const runnerWallet = this.registry.get("runnerWallet") as Wallet | undefined;
      if (runnerWallet) return runnerWallet;
    }
    let snapshot = this.registry.get("launcherWalletSnapshot") as WalletSnapshot | undefined;
    if (!snapshot) {
      snapshot = makeDefaultWalletSnapshot();
      this.registry.set("launcherWalletSnapshot", snapshot);
    }
    return new LauncherWallet(snapshot);
  }

  // -- small utils ----------------------------------------------------------

  private toHex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}
